import React from 'react';
import { GrowingIcon, ShipmentsIcon, WeatherIcon } from '../../../components/icons';
import { orderLayers } from '../../../utils/mapViews';
import { useTheme } from '../../../context/ThemeContext';

const GLYPHS = {
  growing: GrowingIcon,
  shipments: ShipmentsIcon,
  weather: WeatherIcon,
};

// Straight from the design: a 20px disc with an 11px glyph, centres 14px
// apart so the discs overlap by 6. Light 55282:13868, dark 55282:13579.
//
// Dark mode is not a tint of light mode — it inverts the disc to near-black
// green and adds a 1px border the light variant does not have, without which
// the discs lose their edge against the rail's own dark surface.
const DISC = 20;
const STEP = 14;
const GLYPH = 11;
const THEME = {
  light: { bg: '#EAF7EE', glyph: '#15803D', border: 'transparent' },
  dark: { bg: '#04210E', glyph: '#7EE6A4', border: '#343440' },
};

// The exported glyph strokes 0.916667 in an 11-unit viewBox; these icons draw
// in a 20-unit one, so the same on-screen weight is 0.916667 * 20/11.
const GLYPH_STROKE = (0.916667 * 20) / 11;

/**
 * A saved view's icon: one disc per layer, overlapping.
 *
 * The colours come from the theme, not from the tile — the design gives one
 * pair per theme and no selected variant, and both are legible on the rail's
 * surface and on the solid green of a selected tile alike. (An earlier version
 * drove both from `currentColor` and vanished when selected: disc and glyph
 * resolved to the same white.)
 *
 * Discs are laid out left-to-right in DOM order, so the FIRST layer paints on
 * top — the name reads "Growing + Shipments" and the eye meets them in that
 * order.
 */
const MapViewIcon = ({ layers = [], className = '' }) => {
  const { isDark } = useTheme();
  const ids = orderLayers(layers);
  if (!ids.length) return null;

  const theme = isDark ? THEME.dark : THEME.light;
  const width = DISC + STEP * (ids.length - 1);

  return (
    <span
      aria-hidden="true"
      className={`relative inline-block shrink-0 ${className}`}
      style={{ width, height: DISC }}
    >
      {ids.map((id, i) => {
        const Glyph = GLYPHS[id];
        if (!Glyph) return null;
        return (
          <span
            key={id}
            className="absolute top-0 flex items-center justify-center rounded-full border border-solid"
            style={{
              left: i * STEP,
              width: DISC,
              height: DISC,
              backgroundColor: theme.bg,
              borderColor: theme.border,
              // Later discs sit behind, so the first layer stays legible.
              zIndex: ids.length - i,
            }}
          >
            <Glyph
              style={{ width: GLYPH, height: GLYPH, color: theme.glyph }}
              strokeWidth={GLYPH_STROKE}
            />
          </span>
        );
      })}
    </span>
  );
};

export default MapViewIcon;
